import type {
  InvopopJoinStatus,
  InvopopRegistrationMetadata,
  InvopopRegistrationResponse,
  InvopopRegistrationStatus,
  InvopopSupplierStatus,
} from '@/api/invopop-adapter/types.ts'

export type RegistrationUiStatus =
  | 'not_registered'
  | 'processing'
  | 'pending_approval'
  | 'join_pending'
  | 'join_rejected'
  | 'active'
  | 'pending_unregister'
  | 'error'

export type RegistrationRole = 'primary' | 'subsequent'

export const REGISTRATION_ROLE = {
  PRIMARY: 'primary',
  SUBSEQUENT: 'subsequent',
} as const satisfies Record<string, RegistrationRole>

export const INVOPOP_BUSINESS_ROLE = {
  PRIMARY: 'PRIMARY',
  SUBSEQUENT: 'SUBSEQUENT',
} as const

export const INVOPOP_JOIN_STATUS = {
  JOIN_PENDING: 'JOIN_PENDING',
  JOIN_REJECTED: 'JOIN_REJECTED',
  JOIN_BLOCKED: 'JOIN_BLOCKED',
} as const satisfies Record<string, InvopopJoinStatus>

export const BUSINESS_ROLE_TO_REGISTRATION_ROLE = {
  [INVOPOP_BUSINESS_ROLE.PRIMARY]: REGISTRATION_ROLE.PRIMARY,
  [INVOPOP_BUSINESS_ROLE.SUBSEQUENT]: REGISTRATION_ROLE.SUBSEQUENT,
} as const satisfies Record<string, RegistrationRole>

export type RegistrationState = {
  status: RegistrationUiStatus
  raw_status: InvopopRegistrationStatus | null
  onboarding_status: string | null
  registration_iframe_url: string | null
  registration_role: RegistrationRole | null
  pending_join_count: number
  tax_id: string | null
  last_error: string | null
}

export type RegistrationFailureLogDetails = {
  adapter_status: InvopopRegistrationStatus | null
  silo_entry_id: string | null
  job_id: string | null
  has_rejection_reason: boolean
}

export type RegistrationFailureLogEntry = {
  source: string
  reason: string
  details: RegistrationFailureLogDetails
  recorded_at: string
}

export const REGISTRATION_STATUS_KEYS: Record<RegistrationUiStatus, string> = {
  not_registered: 'payments.verifactu.registration.status.not_registered',
  processing: 'payments.verifactu.registration.status.processing',
  pending_approval: 'payments.verifactu.registration.status.pending_approval',
  join_pending: 'payments.verifactu.registration.status.join_pending',
  join_rejected: 'payments.verifactu.registration.status.join_rejected',
  active: 'payments.verifactu.registration.status.active',
  pending_unregister: 'payments.verifactu.registration.status.pending_unregister',
  error: 'payments.verifactu.registration.status.error',
}

export const REGISTRATION_UI_STATUS = {
  NOT_REGISTERED: 'not_registered',
  PROCESSING: 'processing',
  PENDING_APPROVAL: 'pending_approval',
  JOIN_PENDING: 'join_pending',
  JOIN_REJECTED: 'join_rejected',
  ACTIVE: 'active',
  PENDING_UNREGISTER: 'pending_unregister',
  ERROR: 'error',
} as const satisfies Record<string, RegistrationUiStatus>

export const REGISTRATION_POLL_INTERVAL_MS = 3000
export const REGISTRATION_POLL_MAX_ATTEMPTS = 200

const JOIN_HIERARCHY_STATUSES = new Set<InvopopJoinStatus>([
  INVOPOP_JOIN_STATUS.JOIN_PENDING,
  INVOPOP_JOIN_STATUS.JOIN_REJECTED,
])
const PENDING_UNREGISTER_ADAPTER_STATUSES = new Set<InvopopSupplierStatus>(['REJECTED'])
const RETRYABLE_FAILURE_ADAPTER_STATUSES = new Set<InvopopSupplierStatus>(['ERROR'])
const IN_PROGRESS_STATUSES = new Set<InvopopSupplierStatus>(['STARTED', 'PROCESSING', 'WAITING_FOR_UPLOAD'])
const COMPLETED_ADAPTER_STATUSES = new Set<InvopopSupplierStatus>(['REGISTERED', 'APPROVED'])

export const FALLBACK_REGISTRATION_FAILURE_REASON = 'an unexpected system validation error'

const SUPPLIER_STATUS_MAP: Record<InvopopSupplierStatus, RegistrationUiStatus> = {
  STARTED: REGISTRATION_UI_STATUS.PROCESSING,
  PROCESSING: REGISTRATION_UI_STATUS.PROCESSING,
  WAITING_FOR_UPLOAD: REGISTRATION_UI_STATUS.PROCESSING,
  REGISTERED: REGISTRATION_UI_STATUS.PENDING_APPROVAL,
  APPROVED: REGISTRATION_UI_STATUS.ACTIVE,
  REJECTED: REGISTRATION_UI_STATUS.PENDING_UNREGISTER,
  UNREGISTERED: REGISTRATION_UI_STATUS.NOT_REGISTERED,
  ERROR: REGISTRATION_UI_STATUS.ERROR,
  WRONG_TAX_ID: REGISTRATION_UI_STATUS.ERROR,
}

const JOIN_STATUS_MAP: Record<InvopopJoinStatus, RegistrationUiStatus> = {
  [INVOPOP_JOIN_STATUS.JOIN_PENDING]: REGISTRATION_UI_STATUS.JOIN_PENDING,
  [INVOPOP_JOIN_STATUS.JOIN_REJECTED]: REGISTRATION_UI_STATUS.JOIN_REJECTED,
  [INVOPOP_JOIN_STATUS.JOIN_BLOCKED]: REGISTRATION_UI_STATUS.ERROR,
}

export function hasPriorRegistrationAttempt (
  registration: Pick<RegistrationState, 'status'>
): boolean {
  return registration.status !== REGISTRATION_UI_STATUS.NOT_REGISTERED
}

export function createEmptyRegistrationState (): RegistrationState {
  return {
    status: REGISTRATION_UI_STATUS.NOT_REGISTERED,
    raw_status: null,
    onboarding_status: null,
    registration_iframe_url: null,
    registration_role: null,
    pending_join_count: 0,
    tax_id: null,
    last_error: null,
  }
}

function isJoinStatus (status: string): status is InvopopJoinStatus {
  return JOIN_HIERARCHY_STATUSES.has(status as InvopopJoinStatus)
    || status === INVOPOP_JOIN_STATUS.JOIN_BLOCKED
}

function normalizeRawStatus (status: string): InvopopRegistrationStatus | null {
  const normalized = status.toUpperCase()

  if (normalized in SUPPLIER_STATUS_MAP) {
    return normalized as InvopopSupplierStatus
  }

  if (isJoinStatus(normalized)) {
    return normalized as InvopopJoinStatus
  }

  return null
}

export function resolveOnboardingStatusFromPayload (payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const data = payload as Record<string, unknown>
  if (typeof data.onboarding_status === 'string') {
    return data.onboarding_status.toUpperCase()
  }

  const detail = data.detail
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const detailOnboarding = (detail as Record<string, unknown>).onboarding_status
    if (typeof detailOnboarding === 'string') {
      return detailOnboarding.toUpperCase()
    }
  }

  return null
}

export function resolveAdapterStatusFromPayload (payload: unknown): InvopopRegistrationStatus | null {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const data = payload as Record<string, unknown>
  if (typeof data.status === 'string') {
    return normalizeRawStatus(data.status)
  }

  const detail = data.detail
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const detailStatus = (detail as Record<string, unknown>).status
    if (typeof detailStatus === 'string') {
      return normalizeRawStatus(detailStatus)
    }
  }

  return null
}

export function isJoinHierarchyOutcome (payload: unknown): boolean {
  const rawStatus = resolveAdapterStatusFromPayload(payload)
  const onboardingStatus = resolveOnboardingStatusFromPayload(payload)

  if (rawStatus && isJoinStatus(rawStatus)) {
    return rawStatus === INVOPOP_JOIN_STATUS.JOIN_PENDING
      || rawStatus === INVOPOP_JOIN_STATUS.JOIN_REJECTED
  }

  return onboardingStatus === INVOPOP_JOIN_STATUS.JOIN_PENDING
    || onboardingStatus === INVOPOP_JOIN_STATUS.JOIN_REJECTED
}

export function resolveRegistrationRoleFromPayload (
  payload: Partial<InvopopRegistrationResponse> | null | undefined
): RegistrationRole | null {
  if (!payload) {
    return null
  }

  if (payload.business_role && payload.business_role in BUSINESS_ROLE_TO_REGISTRATION_ROLE) {
    return BUSINESS_ROLE_TO_REGISTRATION_ROLE[payload.business_role]
  }

  if (payload.is_primary === true) {
    return REGISTRATION_ROLE.PRIMARY
  }

  if (payload.is_primary === false) {
    return REGISTRATION_ROLE.SUBSEQUENT
  }

  return null
}

function resolveUiStatusFromPayload (
  rawStatus: InvopopRegistrationStatus | null,
  onboardingStatus: string | null
): RegistrationUiStatus {
  if (onboardingStatus && onboardingStatus in JOIN_STATUS_MAP) {
    return JOIN_STATUS_MAP[onboardingStatus as InvopopJoinStatus]
  }

  if (rawStatus && isJoinStatus(rawStatus)) {
    return JOIN_STATUS_MAP[rawStatus]
  }

  if (rawStatus && rawStatus in SUPPLIER_STATUS_MAP) {
    return SUPPLIER_STATUS_MAP[rawStatus as InvopopSupplierStatus]
  }

  return REGISTRATION_UI_STATUS.NOT_REGISTERED
}

export function normalizeRegistrationResponse (
  data: Partial<InvopopRegistrationResponse> & {
    last_error?: string | null
    detail?: string | Record<string, unknown> | null
    tax_id?: string | null
  } = {},
  options: { role?: RegistrationRole | null } = {}
): RegistrationState {
  const rawStatus = resolveAdapterStatusFromPayload(data)
  const onboardingStatus = resolveOnboardingStatusFromPayload(data)

  let status = resolveUiStatusFromPayload(rawStatus, onboardingStatus)

  const hasRejection = hasAdapterRejectionReason(data)
  if (
    hasRejection
    && rawStatus
    && !isJoinStatus(rawStatus)
    && IN_PROGRESS_STATUSES.has(rawStatus as InvopopSupplierStatus)
  ) {
    status = REGISTRATION_UI_STATUS.ERROR
  }

  const registrationUrl = data.registration_url || null
  const registrationIframeUrl = rawStatus && !isJoinStatus(rawStatus) && (
    COMPLETED_ADAPTER_STATUSES.has(rawStatus as InvopopSupplierStatus)
    || rawStatus === 'UNREGISTERED'
    || PENDING_UNREGISTER_ADAPTER_STATUSES.has(rawStatus as InvopopSupplierStatus)
    || (hasRejection && IN_PROGRESS_STATUSES.has(rawStatus as InvopopSupplierStatus))
  )
    ? null
    : registrationUrl

  const role = options.role
    ?? resolveRegistrationRoleFromPayload(data)
    ?? null

  return {
    status,
    raw_status: rawStatus,
    onboarding_status: onboardingStatus,
    registration_iframe_url: status === REGISTRATION_UI_STATUS.JOIN_PENDING
      || status === REGISTRATION_UI_STATUS.JOIN_REJECTED
      ? null
      : registrationIframeUrl,
    registration_role: role,
    pending_join_count: typeof data.pending_join_count === 'number' ? data.pending_join_count : 0,
    tax_id: typeof data.tax_id === 'string' ? data.tax_id : null,
    last_error: data.last_error || (typeof data.detail === 'string' ? data.detail : null) || null,
  }
}

export function isPendingUnregisterAdapterStatus (status: string | null | undefined): boolean {
  return typeof status === 'string' && PENDING_UNREGISTER_ADAPTER_STATUSES.has(status.toUpperCase() as InvopopSupplierStatus)
}

export function isRetryableFailureAdapterStatus (status: string | null | undefined): boolean {
  return typeof status === 'string' && RETRYABLE_FAILURE_ADAPTER_STATUSES.has(status.toUpperCase() as InvopopSupplierStatus)
}

export function isUnregisteredAdapterStatus (status: string | null | undefined): boolean {
  return typeof status === 'string' && status.toUpperCase() === 'UNREGISTERED'
}

export function hasAdapterRejectionReason (payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') {
    return false
  }

  const reason = (payload as Record<string, unknown>).rejection_reason
  return typeof reason === 'string' && reason.trim().length > 0
}

export function isInProgressAdapterStatus (status: string | null | undefined): boolean {
  return typeof status === 'string' && IN_PROGRESS_STATUSES.has(status.toUpperCase() as InvopopSupplierStatus)
}

export function isCompletedAdapterStatus (status: string | null | undefined): boolean {
  return typeof status === 'string' && COMPLETED_ADAPTER_STATUSES.has(status.toUpperCase() as InvopopSupplierStatus)
}

export type RegistrationFailureKind = 'pending_unregister' | 'retryable'

export const REGISTRATION_FAILURE_KIND = {
  PENDING_UNREGISTER: 'pending_unregister',
  RETRYABLE: 'retryable',
} as const satisfies Record<string, RegistrationFailureKind>

export function resolveRegistrationFailureKind (payload: unknown): RegistrationFailureKind | null {
  if (isJoinHierarchyOutcome(payload)) {
    return null
  }

  const status = resolveAdapterStatusFromPayload(payload)

  if (isCompletedAdapterStatus(status)) {
    return null
  }

  if (isPendingUnregisterAdapterStatus(status)) {
    return REGISTRATION_FAILURE_KIND.PENDING_UNREGISTER
  }

  if (isRetryableFailureAdapterStatus(status)) {
    return REGISTRATION_FAILURE_KIND.RETRYABLE
  }

  if (!hasAdapterRejectionReason(payload)) {
    return null
  }

  if (isUnregisteredAdapterStatus(status)) {
    return REGISTRATION_FAILURE_KIND.RETRYABLE
  }

  if (isInProgressAdapterStatus(status)) {
    return REGISTRATION_FAILURE_KIND.RETRYABLE
  }

  return REGISTRATION_FAILURE_KIND.RETRYABLE
}

function readMetadataObject (payload: unknown): InvopopRegistrationMetadata | null {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const data = payload as Record<string, unknown>
  if (data.metadata && typeof data.metadata === 'object' && !Array.isArray(data.metadata)) {
    return data.metadata as InvopopRegistrationMetadata
  }

  const detail = data.detail
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const detailRecord = detail as Record<string, unknown>
    if (detailRecord.metadata && typeof detailRecord.metadata === 'object') {
      return detailRecord.metadata as InvopopRegistrationMetadata
    }
  }

  return null
}

function readReasonFromMetadata (metadata: InvopopRegistrationMetadata | null): string | null {
  if (!metadata) {
    return null
  }

  const candidates = [metadata.reason, metadata.rejection_reason, metadata.message, metadata.description]
  const reason = candidates.find((value) => typeof value === 'string' && value.trim())
  return reason ? reason.trim() : null
}

function readReasonFromInvopopErrors (detail: Record<string, unknown>): string | null {
  const errors = detail.errors
  if (!Array.isArray(errors) || !errors.length) {
    return null
  }

  const message = errors
    .map((entry) => (entry as { message?: string })?.message)
    .filter(Boolean)
    .join('; ')

  return message || null
}

export function parseRegistrationMetadataReason (payload: unknown): string | null {
  const metadata = readMetadataObject(payload)
  const metadataReason = readReasonFromMetadata(metadata)
  if (metadataReason) {
    return metadataReason
  }

  if (!payload || typeof payload !== 'object') {
    return null
  }

  const data = payload as Record<string, unknown>

  if (typeof data.rejection_reason === 'string' && data.rejection_reason.trim()) {
    return data.rejection_reason.trim()
  }

  const detail = (data.detail ?? data) as Record<string, unknown>

  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const detailMetadataReason = readReasonFromMetadata(detail.metadata as InvopopRegistrationMetadata | null)
    if (detailMetadataReason) {
      return detailMetadataReason
    }

    const invopopReason = readReasonFromInvopopErrors(detail)
    if (invopopReason) {
      return invopopReason
    }

    if (typeof detail.message === 'string' && detail.message.trim()) {
      return detail.message.trim()
    }
  }

  if (typeof data.last_error === 'string' && data.last_error.trim()) {
    return data.last_error.trim()
  }

  return null
}

export function extractRegistrationFailure (payload: unknown) {
  const metadata = readMetadataObject(payload)
  const reason = parseRegistrationMetadataReason(payload) || FALLBACK_REGISTRATION_FAILURE_REASON

  return { metadata, reason, raw: payload }
}

export function extractRegistrationFailureDetails (payload: unknown): RegistrationFailureLogDetails {
  if (!payload || typeof payload !== 'object') {
    return {
      adapter_status: null,
      silo_entry_id: null,
      job_id: null,
      has_rejection_reason: false,
    }
  }

  const data = payload as Record<string, unknown>

  return {
    adapter_status: typeof data.status === 'string'
      ? normalizeRawStatus(data.status)
      : resolveAdapterStatusFromPayload(payload),
    silo_entry_id: typeof data.silo_entry_id === 'string' ? data.silo_entry_id : null,
    job_id: typeof data.job_id === 'string' ? data.job_id : null,
    has_rejection_reason: hasAdapterRejectionReason(payload),
  }
}

export function createRegistrationFailureLogEntry (
  failure: ReturnType<typeof extractRegistrationFailure>,
  source: string
): RegistrationFailureLogEntry {
  return {
    source,
    reason: failure.reason,
    details: extractRegistrationFailureDetails(failure.raw),
    recorded_at: new Date().toISOString(),
  }
}

export function toRegistrationFailureLoggerPayload (entry: RegistrationFailureLogEntry) {
  return {
    source: entry.source,
    adapter_status: entry.details.adapter_status,
    silo_entry_id: entry.details.silo_entry_id,
    job_id: entry.details.job_id,
    has_rejection_reason: entry.details.has_rejection_reason,
    recorded_at: entry.recorded_at,
  }
}

export function getSupplierRegistrationBlockedDetail (payload: unknown) {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const data = payload as Record<string, unknown>
  const detail = (data.detail ?? data) as Record<string, unknown>
  if (detail?.code !== 'SUPPLIER_REGISTRATION_FAILED') {
    return null
  }

  return detail
}

export function isSupplierRegistrationFailedPayload (payload: unknown): boolean {
  return getSupplierRegistrationBlockedDetail(payload) !== null
}

export function isSubsequentJoinBlockedError (
  error: { response?: { data?: unknown } },
  wasRegisteredBefore: boolean
): boolean {
  if (wasRegisteredBefore) {
    return false
  }

  return isSupplierRegistrationFailedPayload(error?.response?.data)
}

export function isPrimaryRegistrationBlockedError (
  error: { response?: { data?: unknown } },
  wasRegisteredBefore: boolean
): boolean {
  if (!wasRegisteredBefore) {
    return false
  }

  return isSupplierRegistrationFailedPayload(error?.response?.data)
}

export function isRegistrationInProgressError (error: { response?: { status?: number; data?: unknown } }): boolean {
  const status = error?.response?.status
  if (status !== 423 && status !== 409) {
    return false
  }

  const data = error?.response?.data as Record<string, unknown> | undefined
  if (!data) {
    return false
  }

  const detail = data.detail as Record<string, unknown> | undefined
  const code = data.error ?? detail?.error ?? detail?.code
  return code === 'registration_in_progress'
}

export function detectRegistrationRole ({
  putResponse,
  getResponse,
  wasRegisteredBefore,
}: {
  putResponse: Partial<InvopopRegistrationResponse> | null
  getResponse: Partial<InvopopRegistrationResponse> | null
  wasRegisteredBefore: boolean
}): RegistrationRole {
  const fromApi = resolveRegistrationRoleFromPayload(putResponse)
    ?? resolveRegistrationRoleFromPayload(getResponse)

  if (fromApi) {
    return fromApi
  }

  if (isJoinHierarchyOutcome(putResponse) || isJoinHierarchyOutcome(getResponse)) {
    return REGISTRATION_ROLE.SUBSEQUENT
  }

  if (putResponse?.registration_url) {
    return REGISTRATION_ROLE.PRIMARY
  }

  if (!putResponse?.registration_url && getResponse?.registration_url) {
    return REGISTRATION_ROLE.SUBSEQUENT
  }

  const putStatus = putResponse?.status?.toUpperCase() as InvopopRegistrationStatus | undefined
  if (putStatus && COMPLETED_ADAPTER_STATUSES.has(putStatus as InvopopSupplierStatus) && !wasRegisteredBefore) {
    return REGISTRATION_ROLE.SUBSEQUENT
  }

  return REGISTRATION_ROLE.PRIMARY
}

export function isJoinPendingResponse (payload: unknown): boolean {
  return isJoinHierarchyOutcome(payload)
    && resolveOnboardingStatusFromPayload(payload) !== INVOPOP_JOIN_STATUS.JOIN_REJECTED
    && resolveAdapterStatusFromPayload(payload) !== INVOPOP_JOIN_STATUS.JOIN_REJECTED
}

export function isTaxIdValidationError (error: { response?: { data?: { detail?: unknown } } }): boolean {
  const detail = error?.response?.data?.detail
  if (!Array.isArray(detail)) {
    return false
  }

  return detail.some((entry) => (entry as { loc?: string[] })?.loc?.includes?.('tax_id'))
}

export function parseInvopopRegistrationError (error: { response?: { data?: { detail?: unknown } } }): string | null {
  const detail = error?.response?.data?.detail

  if (typeof detail === 'string' && detail.trim()) {
    return detail
  }

  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const record = detail as Record<string, unknown>
    if (typeof record.message === 'string' && record.message.trim()) {
      return record.message.trim()
    }
  }

  if (Array.isArray(detail)) {
    const taxIdError = detail.find((entry) => (entry as { loc?: string[] })?.loc?.includes?.('tax_id'))
    const message = (taxIdError as { msg?: string })?.msg
      || detail.map((entry) => (entry as { msg?: string })?.msg).filter(Boolean).join(' ')
    if (message) {
      return message
    }
  }

  return null
}
